/**
 * A lista de avisos (E66.F3, ADR-0071), a mesma no portal e na equipe (E150.F1 — GUS-150). Cada
 * área diz de onde lê, como marca, para onde o aviso leva e os seus textos (`OrigemDosAvisos`).
 *
 * O contador vem do servidor (a mesma lista, BR-10). Marcar como lido trava o contexto durante a
 * mutação e usa a lista que a resposta traz; depois avisa o menu, que relê o contador.
 */
import { useCallback, useEffect, useState } from 'react';
import type { AxiosInstance } from 'axios';
import { Link } from 'react-router-dom';

import { Badge, Blueprint, Button, EmptyState, MonoData, Skeleton } from '@/components/ui';
import { useApiClient } from '@/lib/api';
import {
  EVENTO_AVISOS_ALTERADOS,
  type AvisoData,
  type AvisosData,
  type AvisoTipo,
} from '@/lib/portal/avisos';

import {
  chaveDoAviso,
  detalheDoAviso,
  formatarDia,
  varianteDoTipo,
  type TextosDosAvisos,
} from './rotulosDoAviso';

export interface OrigemDosAvisos {
  buscar: (client: AxiosInstance) => Promise<AvisosData>;
  marcar: (
    client: AxiosInstance,
    aviso: { tipo: AvisoTipo; objeto_id: string },
  ) => Promise<AvisosData>;
  linkDoAviso: (aviso: AvisoData) => string;
  rotuloDoTipo: (tipo: string) => string;
  textos: TextosDosAvisos;
  testId: string;
}

function AvisoItem({
  aviso,
  origem,
  pendente,
  onMarcar,
}: {
  aviso: AvisoData;
  origem: OrigemDosAvisos;
  pendente: boolean;
  onMarcar: (aviso: AvisoData) => void;
}) {
  const t = origem.textos;
  return (
    <li>
      <Blueprint
        data-testid="aviso-item"
        data-tipo={aviso.tipo}
        data-visto={aviso.visto ? 'true' : 'false'}
        className={`p-sc-4 ${aviso.visto ? '' : 'border-l-2 border-l-accent'}`}
      >
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <Badge variant={varianteDoTipo(aviso.tipo)}>{origem.rotuloDoTipo(aviso.tipo)}</Badge>
            <p className="mt-2 text-body-sm text-fg-muted">{detalheDoAviso(aviso, t)}</p>
          </div>
          <MonoData className="text-caption tabular-nums text-fg-dim">
            {formatarDia(aviso.ocorrido_em_date)}
          </MonoData>
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-3">
          <Link
            to={origem.linkDoAviso(aviso)}
            data-testid="aviso-abrir-caso"
            className="text-body-sm font-semibold text-accent-700 hover:underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
          >
            {t.abrirCaso}
          </Link>
          {aviso.visto ? (
            <span className="text-caption text-fg-dim" data-testid="aviso-lido">
              {t.marcado}
            </span>
          ) : (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              data-testid="aviso-marcar"
              // Trava o contexto durante a mutação: enquanto o POST está em voo
              // o botão não aceita segundo clique, e a resposta que chega é a
              // do pedido que saiu ([[feedback_trave_o_contexto_durante_a_mutacao]]).
              disabled={pendente}
              onClick={() => onMarcar(aviso)}
            >
              {t.marcarVisto}
            </Button>
          )}
        </div>
      </Blueprint>
    </li>
  );
}

/** A leitura e a marcação dos avisos, com o contexto travado durante a mutação. O portal 6.8
 * (E165.F2.T06) usa a mesma leitura com o desenho próprio. */
export function useAvisos(origem: Pick<OrigemDosAvisos, 'buscar' | 'marcar'>) {
  const client = useApiClient();
  const [avisos, setAvisos] = useState<AvisosData | null>(null);
  const [erro, setErro] = useState(false);
  const [erroMarcar, setErroMarcar] = useState(false);
  const [pendente, setPendente] = useState<string | null>(null);
  const { buscar, marcar: marcarNoServidor } = origem;

  useEffect(() => {
    let cancelled = false;
    buscar(client)
      .then((data) => {
        if (!cancelled) setAvisos(data);
      })
      .catch(() => {
        if (!cancelled) setErro(true);
      });
    return () => {
      cancelled = true;
    };
  }, [client, buscar]);

  const marcar = useCallback(
    (aviso: AvisoData) => {
      // Um pedido de cada vez: a resposta é a lista INTEIRA, então duas
      // marcações em voo poderiam se sobrescrever fora de ordem.
      if (pendente !== null) return;
      setPendente(chaveDoAviso(aviso));
      setErroMarcar(false);
      marcarNoServidor(client, { tipo: aviso.tipo, objeto_id: aviso.objeto_id })
        .then((data) => {
          setAvisos(data);
          window.dispatchEvent(new Event(EVENTO_AVISOS_ALTERADOS));
        })
        .catch(() => setErroMarcar(true))
        .finally(() => setPendente(null));
    },
    [client, marcarNoServidor, pendente],
  );
  return { avisos, erro, erroMarcar, pendente, marcar };
}

export function ListaDeAvisos({ origem }: { origem: OrigemDosAvisos }) {
  const t = origem.textos;
  const { avisos, erro, erroMarcar, pendente, marcar } = useAvisos(origem);

  if (erro) {
    return (
      <p role="alert" className="mt-8 text-body-sm text-danger">
        {t.erroCarregar}
      </p>
    );
  }
  if (avisos === null) {
    return (
      <div className="mt-8 space-y-3" aria-hidden>
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-24 w-full" />
      </div>
    );
  }
  return (
    <div data-testid={origem.testId}>
      <p className="mt-6 text-body-sm text-fg-muted" data-testid="avisos-contador">
        {avisos.nao_vistos === 0
          ? t.contadorTudoLido
          : t.contadorNaoVistos.replace('{n}', String(avisos.nao_vistos))}
      </p>
      {erroMarcar && (
        <p role="alert" className="mt-3 text-body-sm text-danger">
          {t.marcarErro}
        </p>
      )}
      {avisos.itens.length === 0 ? (
        <EmptyState
          data-testid="avisos-vazio"
          className="mt-6"
          title={t.vazioTitulo}
          description={t.vazioTexto}
        />
      ) : (
        <ul className="mt-6 space-y-3" data-testid="avisos-lista">
          {avisos.itens.map((aviso) => (
            <AvisoItem
              key={chaveDoAviso(aviso)}
              aviso={aviso}
              origem={origem}
              pendente={pendente !== null}
              onMarcar={marcar}
            />
          ))}
        </ul>
      )}
    </div>
  );
}
